import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useOnboardingGate } from "@/components/routing/OnboardingGate";
import { useCurrentPageTitle } from "@/contexts/PageTitleContext";
import { useGuideProgress } from "@/hooks/useGuideProgress";
import { ONBOARDING_ORANGE, ONBOARDING_ORANGE_SHADOW } from "./onboardingChrome";

/**
 * ¿Hay abierto algún diálogo, hoja o menú? Radix marca `data-state` en contenido
 * que vive en un portal, así que se vigila `document.body` entero en vez de mirar
 * un árbol concreto.
 */
function useAnyOverlayOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      const hit = document.querySelector(
        // `[data-vaul-drawer]` se fue con vaul: ya no lo pinta nadie.
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-radix-popper-content-wrapper] [data-state="open"]',
      );
      setOpen(!!hit);
    };
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
      childList: true,
    });
    return () => mo.disconnect();
  }, []);
  return open;
}

/**
 * Vuelta a la guía desde móvil y tablet.
 *
 * En escritorio la guía está siempre en el lateral. Por debajo de `lg` no había
 * NADA: quien entraba en «Añadir miembros del equipo» se quedaba sin camino de
 * vuelta —ni menú inferior (`MobileDock` se oculta a propósito durante la puesta en
 * marcha, para que nadie se la salte navegando), ni barra superior de progreso
 * (`OnboardingProgressBar` no llega a pintarse nunca: su mapa de rutas está vacío)—.
 * Este componente existía desde hace tiempo pero no lo importaba nadie.
 *
 * Ocupa EXACTAMENTE el hueco del menú inferior —misma anchura, misma altura, mismo
 * radio, misma separación del borde— porque durante la puesta en marcha ese hueco
 * está libre. Así el pulgar ya sabe dónde está sin tener que aprender un sitio nuevo.
 *
 * Se esconde mientras haya un diálogo o una hoja abiertos: es una ayuda para
 * moverse, no algo que deba taparle a nadie el botón de guardar.
 */
export function OnboardingReturnBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { active } = useOnboardingGate();
  const { hideMobileDock } = useCurrentPageTitle();
  const anyOverlayOpen = useAnyOverlayOpen();
  const { completedCount, totalCount } = useGuideProgress();

  if (!active) return null;
  if (anyOverlayOpen) return null;
  /* Las pantallas que esconden el menú inferior también dejan de reservar el hueco
     de abajo (`body.dock-hidden`), así que aquí la barra taparía contenido. */
  if (hideMobileDock) return null;
  /* Solo se esconde donde la guía YA está en pantalla. La versión anterior también
     se apagaba en todo `/settings`, que incluye `/settings/structure` — el primer
     paso de los seis, justo donde más falta hace. */
  if (location.pathname === "/getting-started") return null;
  if (location.pathname === "/welcome") return null;

  return (
    <div
      /* Misma marca que el resto de barras inferiores: es lo que hace que el CSS
         `body.keyboard-open [data-app-dock]` la quite cuando sale el teclado. */
      data-app-dock
      /* `lg:hidden` y no `useIsMobile()`: es la misma regla que usa el menú inferior,
         y en CSS no hay un primer render en el que la barra parpadee. */
      className="lg:hidden fixed left-0 right-0 z-[45] pointer-events-none"
      style={{ bottom: "var(--dock-bottom)" }}
    >
      <div className="mx-auto max-w-md md:max-w-3xl px-[14px]">
        <button
          onClick={() => navigate("/getting-started")}
          aria-label={t("guide.returnBar.label")}
          className="pointer-events-auto relative w-full h-16 rounded-[24px] overflow-hidden flex items-center gap-3 px-3 text-white active:scale-[0.98] transition-transform"
          style={{ background: ONBOARDING_ORANGE, boxShadow: ONBOARDING_ORANGE_SHADOW }}
        >
          {/* Flecha en un círculo blanco: el gesto es «volver», y tiene que leerse
              antes que el texto. */}
          <span className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center">
            <ArrowLeft className="w-5 h-5 text-[#EA580C]" strokeWidth={2.5} />
          </span>

          <span className="flex-1 min-w-0 text-left">
            <span className="block text-[15px] font-semibold leading-tight truncate">
              {t("guide.returnBar.label")}
            </span>
            <span className="block text-[11.5px] leading-tight text-white/90 truncate">
              {t("guide.progress", { completed: completedCount, total: totalCount })}
            </span>
          </span>

          <span className="flex-shrink-0 text-[15px] font-bold tabular-nums pe-1">
            {completedCount}/{totalCount}
          </span>
        </button>
      </div>
    </div>
  );
}
